// SollIstCounter — Mini-Anzeige der Soll/Ist-Besetzung pro Fahrzeug für eine Diensttag-Spalte
// Zeigt ALLE aktiven Fahrzeuge des Diensttages mit dynamischer Nummerierung
// (z.B. RTW1, RTW2, RTW3 bei mehreren RTW; GW-H bleibt einzeln).
import React from 'react';
import { cn } from '../../lib/utils';
import { Tooltip, TooltipContent, TooltipTrigger } from '../ui/tooltip';

const getKurzName = (fzName) => {
  if (!fzName) return '?';
  // Funkrufname-Format z.B. "RTW 4600/1" → Typ-Teil "RTW"
  // GW Hygiene → "GW-H"
  const parts = fzName.split(' ');
  if (parts.length === 0) return fzName;
  const typTeil = parts[0];
  // Wenn 2. Wort kein Zahlen-Block ist (z.B. "Hygiene"), nutze 1. Buchstabe als Suffix
  if (parts.length > 1) {
    const zweites = parts[1];
    const istNurZahlBlock = /^\d+/.test(zweites);
    if (!istNurZahlBlock) {
      return `${typTeil}-${zweites.charAt(0).toUpperCase()}`;
    }
  }
  return typTeil;
};

export function SollIstCounter({ diensttag, sollIst }) {
  if (!sollIst) return null;

  const key = `${diensttag.datum}_${diensttag.dienst_art}`;
  const data = sollIst[key];

  if (!data || !data.fahrzeuge) return null;

  // Nur aktive im Dienst — keine Limitierung mehr, alle Fahrzeuge zeigen
  const aktiveFahrzeuge = data.fahrzeuge.filter(fz => fz.ist_aktiv_in_dienst);

  // Gruppen nach Kurzname zählen, damit gleiche Kurznamen durchnummeriert werden
  const kurznameCount = {};
  aktiveFahrzeuge.forEach(fz => {
    const kn = getKurzName(fz.name);
    kurznameCount[kn] = (kurznameCount[kn] || 0) + 1;
  });

  // Laufende Indizes pro Gruppe
  const kurznameIndex = {};

  return (
    <div className="flex flex-col gap-1 py-2 px-1">
      {aktiveFahrzeuge.map((fz, i) => {
        const ist = fz.ist || 0;
        const soll = fz.soll || 0;
        const diff = ist - soll;
        // Farb-Logik:
        // - Sollstärke 0 (Fahrzeug optional besetzbar, z.B. DLK) → immer neutral grau,
        //   auch wenn Personal fest eingeteilt ist (kein „Überbesetzt"-Signal).
        // - Sonst: rot bei Fehlbesetzung, amber bei Überhang, grün bei genau erfüllt.
        let bgColor;
        if (soll === 0) {
          bgColor = ist > 0
            ? 'bg-slate-200 text-slate-700'   // besetzt aber optional
            : 'bg-slate-100 text-slate-500';  // leer + optional
        } else if (diff < 0) {
          bgColor = 'bg-red-100 text-red-800';
        } else if (diff > 0) {
          bgColor = 'bg-amber-100 text-amber-800';
        } else {
          bgColor = 'bg-emerald-100 text-emerald-800';
        }

        const baseKurz = getKurzName(fz.name);
        let kurzName = baseKurz;
        if (kurznameCount[baseKurz] > 1) {
          kurznameIndex[baseKurz] = (kurznameIndex[baseKurz] || 0) + 1;
          kurzName = `${baseKurz}${kurznameIndex[baseKurz]}`;
        }

        return (
          <Tooltip key={i}>
            <TooltipTrigger asChild>
              <div className={cn('text-[9px] sm:text-[10px] px-1 py-0.5 rounded font-medium text-center leading-tight', bgColor)}>
                <div className="font-bold truncate">{kurzName}</div>
                <div className={cn('font-mono', soll === 0 && ist > 0 && 'font-semibold')}>
                  {soll === 0 ? (ist > 0 ? `${ist} · opt.` : '– opt.') : `${ist}/${soll}`}
                </div>
              </div>
            </TooltipTrigger>
            <TooltipContent side="top">
              <p className="text-xs font-medium">{fz.name}</p>
              <p className="text-xs">Besetzung: {ist} von {soll}</p>
            </TooltipContent>
          </Tooltip>
        );
      })}
    </div>
  );
}

export default SollIstCounter;
